/**
 * Куда поставить подпись площади комнаты, чтобы она не легла на люстру или мебель.
 *
 * Отправная точка — центр комнаты, но там же по правилам висит люстра, а под ней
 * часто стоит стол или тумба. Поэтому перебираем несколько точек вокруг центра
 * и берём первую, где прямоугольник подписи целиком внутри комнаты и не задевает
 * ни один предмет. Не нашли — оставляем центр: подпись с подложкой всё равно читается.
 */
import { getCatalogItem } from "@/shared/catalog";
import { CatalogSectionKind, itemDimensions, type FloorItem, type PlanLayers } from "@/shared/plan";
import { formatArea, pointInPolygon, type Pt } from "@/shared/lib/geometry";
import type { Room } from "./rooms";

/** Осевой прямоугольник предмета на плане, мм */
export interface LabelObstacle {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
}

/** Кегль подписи в пикселях — от него считается её размер в мм при текущем зуме */
export const ROOM_LABEL_FONT_PX = 13;

/** Внутренний отступ белой плашки подписи. */
export const ROOM_LABEL_PADDING_PX = 5;

/** Зазор между отдельными плашками строк подписи. */
export const ROOM_LABEL_LINE_GAP_PX = 3;

/** Зазор между подписью и предметом, мм */
const GAP = 60;

/** Название помещения на плане и в 3D-карте. */
export const roomLabelName = (index: number): string => `ПО ${index + 1}`;

/** Название комнаты: своё, если пользователь его дал, иначе номер «ПО N». */
export const roomDisplayName = (
	rooms: PlanLayers["rooms"],
	roomId: string,
	index: number,
): string => rooms.items[roomId]?.name ?? roomLabelName(index);

/**
 * Полное название комнаты для заголовков — вкладки развёрток, листы PDF, смета: своё,
 * если пользователь его дал, иначе «Помещение N».
 */
export const roomTitle = (rooms: PlanLayers["rooms"], roomId: string, index: number): string =>
	rooms.items[roomId]?.name ?? `Помещение ${index + 1}`;

/** Текст подписи — чистая площадь комнаты. */
export const roomLabelText = (area: number): string => formatArea(area);

/** Отметка чистовой высоты комнаты под площадью. */
export const roomLabelCeilingHeight = (height: number): string => `h ${height}`;

/** Оценка габарита подписи в мм: ширина по числу знаков, высота с подложкой */
export const roomLabelSize = (text: string, mmPerPx: number, lines = 1) => ({
	width: (text.length * ROOM_LABEL_FONT_PX * 0.5 + ROOM_LABEL_PADDING_PX * 2) * mmPerPx,
	height:
		(ROOM_LABEL_FONT_PX * 1.2 * lines +
			ROOM_LABEL_PADDING_PX * 2 * lines +
			ROOM_LABEL_LINE_GAP_PX * (lines - 1)) *
		mmPerPx,
});

/** Габарит предмета с учётом поворота — осевой прямоугольник вокруг него */
const itemBox = (item: FloorItem): LabelObstacle | null => {
	const info = getCatalogItem(item.ref);
	if (!info) return null;
	// Потолочный светильник рисуется как width × height, предмет на полу — width × depth
	const dimensions = itemDimensions(item, info);
	const along = dimensions.width;
	const across =
		item.ref.catalog === CatalogSectionKind.Electrical ||
		item.ref.catalog === CatalogSectionKind.Lighting
			? dimensions.height
			: dimensions.depth;
	const a = (item.rotation * Math.PI) / 180;
	const hw = (Math.abs(Math.cos(a)) * along + Math.abs(Math.sin(a)) * across) / 2;
	const hh = (Math.abs(Math.sin(a)) * along + Math.abs(Math.cos(a)) * across) / 2;
	return { minX: item.x - hw, minY: item.y - hh, maxX: item.x + hw, maxY: item.y + hh };
};

const boxAt = (center: Pt, width: number, height: number): LabelObstacle => ({
	minX: center.x - width / 2,
	minY: center.y - height / 2,
	maxX: center.x + width / 2,
	maxY: center.y + height / 2,
});

const corners = (b: LabelObstacle): Pt[] => [
	{ x: b.minX, y: b.minY },
	{ x: b.maxX, y: b.minY },
	{ x: b.maxX, y: b.maxY },
	{ x: b.minX, y: b.maxY },
];

const overlaps = (a: LabelObstacle, b: LabelObstacle): boolean =>
	a.minX < b.maxX + GAP && a.maxX > b.minX - GAP && a.minY < b.maxY + GAP && a.maxY > b.minY - GAP;

/** Предметы видимых слоёв — на полу и на потолке, — стоящие в этой комнате */
export const roomObstacles = (room: Room, layers: PlanLayers): LabelObstacle[] => {
	const boxes: LabelObstacle[] = [];
	for (const layer of [layers.electrical, layers.plumbing, layers.furniture]) {
		if (!layer.visible) continue;
		for (const item of Object.values(layer.items)) {
			if (!pointInPolygon(item, room.inner)) continue;
			const box = itemBox(item);
			if (box) boxes.push(box);
		}
	}
	return boxes;
};

/**
 * Точка подписи: центр комнаты или ближайшее свободное место рядом.
 * Сначала пробуем сдвиг вниз и вверх, потом в стороны — подпись остаётся у центра.
 * Первый проход требует, чтобы подпись целиком лежала в комнате; если комната узкая
 * и подпись шире неё (коридор при мелком масштабе), второй проход довольствуется
 * центром подписи внутри комнаты.
 */
export const placeRoomLabel = (
	room: Room,
	obstacles: LabelObstacle[],
	width: number,
	height: number,
): Pt => {
	const step = height + GAP;
	const shifts: Pt[] = [{ x: 0, y: 0 }];
	for (let k = 1; k <= 4; k++) {
		shifts.push({ x: 0, y: k * step }, { x: 0, y: -k * step });
	}
	for (let k = 1; k <= 2; k++) {
		shifts.push({ x: k * width, y: 0 }, { x: -k * width, y: 0 });
	}
	const candidates = shifts.map((shift) => ({
		x: room.label.x + shift.x,
		y: room.label.y + shift.y,
	}));
	const free = (center: Pt) => !obstacles.some((o) => overlaps(boxAt(center, width, height), o));
	const wholeInside = (center: Pt) =>
		corners(boxAt(center, width, height)).every((p) => pointInPolygon(p, room.inner));
	return (
		candidates.find((c) => wholeInside(c) && free(c)) ??
		candidates.find((c) => pointInPolygon(c, room.inner) && free(c)) ??
		room.label
	);
};
